import { useCallback, useMemo, useState } from 'react'

import { isEmpty } from 'lodash-es'

import { SumoLogLevel } from '../../common/utils/commonUtils'
import { isWithinPastXMonths } from '../../common/utils/dateUtils'
import { DEFAULT_LANGUAGE } from '../../intl/constants'
import { useLyraIntl } from '../../intl/hooks/useLyraIntl'
import { WellnessCheckInParsedResults, WellnessCheckInResponse, WellnessCheckInResults } from '../types'
import { getWellnessCheckInParsedBody } from '../utils'

interface useWellnessCheckInArgs {
  isWellnessCheckInEnabled: boolean
  isWellnessCheckInBenefitsEntryPointEnabled: boolean
  isHomebaseWellnessCheckInEntrypointEnabled: boolean
  libraryDisabled: boolean
  isUserAssumedInternational?: boolean
  hideWellnessCheckInForLoggedOutUser?: boolean
  storedWellnessCheckInStateResults?: WellnessCheckInResults
  getWellnessCheckInResultsData: () => Promise<WellnessCheckInResponse[]>
  postWellnessCheckInResults: (requestBody: WellnessCheckInResults) => Promise<WellnessCheckInResponse>
  logToSumo: (data: { message: string; type: SumoLogLevel }) => void
  setWellnessCheckInResults: (data: WellnessCheckInParsedResults) => void
  clearWellnessCheckInFormValues: () => void
  clearWellnessCheckInResults: () => void
}

const SUPPORTED_WELLNESS_CHECK_IN_LANGUAGES = [DEFAULT_LANGUAGE]

export const useWellnessCheckIn = ({
  isWellnessCheckInEnabled,
  isWellnessCheckInBenefitsEntryPointEnabled,
  isHomebaseWellnessCheckInEntrypointEnabled,
  libraryDisabled,
  hideWellnessCheckInForLoggedOutUser,
  storedWellnessCheckInStateResults,
  getWellnessCheckInResultsData,
  postWellnessCheckInResults,
  logToSumo,
  setWellnessCheckInResults,
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
}: useWellnessCheckInArgs) => {
  const { activeLanguage } = useLyraIntl()
  const isLanguageSupported = SUPPORTED_WELLNESS_CHECK_IN_LANGUAGES.includes(activeLanguage)
  const [loading, setLoading] = useState(false)

  const showWellnessCheckIn = useMemo(() => {
    return isWellnessCheckInEnabled && !libraryDisabled && !hideWellnessCheckInForLoggedOutUser && isLanguageSupported
  }, [isWellnessCheckInEnabled, libraryDisabled, isLanguageSupported, hideWellnessCheckInForLoggedOutUser])

  /**
   * From list of wellness check in results, get the most recent one, then
   * parse the results to convert the type from string to WellnessCheckInResults,
   * and set it in redux.
   */
  const parseAndSetLatestResults = useCallback(
    (wellnessCheckInResultsList: WellnessCheckInResponse[]) => {
      if (wellnessCheckInResultsList?.length > 0) {
        const lastIndex = wellnessCheckInResultsList.length - 1
        const latestResults = wellnessCheckInResultsList[lastIndex]
        if (latestResults) {
          try {
            const latestWellbeingResponseWithForm = getWellnessCheckInParsedBody(latestResults)
            setWellnessCheckInResults({
              results: latestWellbeingResponseWithForm,
              withinPastThreeMonths: isWithinPastXMonths(new Date(latestResults.updateDate), 3),
              date: latestResults.updateDate,
            })
          } catch (error) {
            logToSumo({
              message:
                error instanceof Error ? error.message : `Unable to parse latest wellbeing response string: ${error}`,
              type: 'ERROR',
            })
          }
        }
      }
    },
    [logToSumo, setWellnessCheckInResults],
  )

  /**
   * Get list of wellness check in results associated with user
   */
  const fetchWellnessCheckInResults = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      const wellnessCheckInResultsList = await getWellnessCheckInResultsData()
      parseAndSetLatestResults(wellnessCheckInResultsList)
    } catch (error) {
      console.error(`Unable to get wellness check in results: ${error}`)
      logToSumo({
        message: error instanceof Error ? error.message : `Unable to get wellness check in results: ${error}`,
        type: 'ERROR',
      })
    } finally {
      setLoading(false)
    }
  }, [getWellnessCheckInResultsData, loading, logToSumo, parseAndSetLatestResults])

  /**
   * Used for the flow where an existing user takes the WCI while logged out, and then logs in from Preview page.
   * (Preview -> Login Page -> Wellness Check In Results)
   * Take the results from redux and save to the BE after the user is logged in.
   */
  const saveLatestCheckInResults = useCallback(async () => {
    if (loading) return
    setLoading(true)
    console.debug('Saving wellness check in results from preview page after user has logged in directly')
    try {
      if (storedWellnessCheckInStateResults) {
        await postWellnessCheckInResults(storedWellnessCheckInStateResults)
      }
    } catch (error) {
      console.error(`Unable to save wellness check in results: ${error}`)
      logToSumo({
        message: error instanceof Error ? error.message : `Unable to save wellness check in results: ${error}`,
        type: 'ERROR',
      })
    } finally {
      setLoading(false)
    }
  }, [loading, logToSumo, postWellnessCheckInResults, storedWellnessCheckInStateResults])

  /**
   * Resets wellness check in values in redux
   */
  const resetWellnessCheckInValues = useCallback(
    async ({
      clearFormValues = true,
      clearResults = true,
    }: { clearFormValues?: boolean; clearResults?: boolean } = {}) => {
      if (clearFormValues) {
        clearWellnessCheckInFormValues()
      }
      if (clearResults && storedWellnessCheckInStateResults && !isEmpty(storedWellnessCheckInStateResults)) {
        const message = 'Detected existing wellness check in results - automatically deleting previous results'
        logToSumo({
          message,
          type: 'INFO',
        })
        console.debug(message)
        clearWellnessCheckInResults()
      }
    },
    [clearWellnessCheckInFormValues, storedWellnessCheckInStateResults, logToSumo, clearWellnessCheckInResults],
  )

  return {
    loading,
    showWellnessCheckIn,
    showWellnessCheckInBenefitsEntryPoint: showWellnessCheckIn && isWellnessCheckInBenefitsEntryPointEnabled,
    showWellnessCheckInHomebaseEntryPoint: showWellnessCheckIn && isHomebaseWellnessCheckInEntrypointEnabled,
    fetchWellnessCheckInResults,
    saveLatestCheckInResults,
    resetWellnessCheckInValues,
  }
}
